import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { ProductToggle as ProductToggleProps, SanityKeyed } from 'types/studio/studio';
import { CommonSectionProps } from '../../types';
import styles from './ProductToggle.module.scss';

const ProductToggle = (props: SanityKeyed<ProductToggleProps> & CommonSectionProps) => {
  const { _key: key, options } = props;
  const router = useRouter();

  // Compute the active option key directly from the URL and options
  const activeOptionKey = useMemo(() => {
    if (!options || options.length === 0) return null;

    // First try to match with current URL
    if (router.asPath) {
      const matchingOption = options.find(
        (option: { url: string }) =>
          router.asPath.includes(option.url) || option.url === router.asPath
      );

      if (matchingOption) {
        return matchingOption._key;
      }
    }

    // Fall back to default option or first option
    const defaultOption = options.find((option) => option.isDefault) || options[0];
    return defaultOption?._key || null;
  }, [options, router.asPath]);

  // Only hide the component if there are no options or only one option
  if (!options || options.length < 2) {
    return null;
  }

  return (
    <div className={styles.container} id={key}>
      <div className={styles.toggleWrapper}>
        {options.map((option) => {
          // Get the base path from the option URL
          const urlPath = option.url;

          // Create a filtered query object, excluding the slug
          const query = { ...router.query };
          delete query.slug; // Remove the slug as it's part of the path, not a query parameter

          // Create a Link that preserves relevant query parameters
          return (
            <Link
              key={option._key}
              href={{
                pathname: urlPath,
                query
              }}
              onClick={(e) => {
                // Prevent navigation if clicking the already active option
                if (activeOptionKey === option._key) {
                  e.preventDefault();
                }
              }}
              className={clsx(
                styles.toggleOption,
                activeOptionKey === option._key ? styles.active : styles.inactive
              )}
              aria-current={activeOptionKey === option._key ? 'page' : undefined}>
              {option.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default ProductToggle;
